import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Button, Space, Table, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CreateUnitCommand,
  UnitCategoryFloorDto,
  UnitDto,
  CreateUpdateUnitFloorAllocationDto,
  UnitBasicInfoDto,
  UnitStatisticsDto,
} from 'api/models';
import { UnitsApi } from 'api/api';
import { formatSquareMeters } from 'utility/utility';
import openNotificationWithIcon from 'utility/notification';
import FeatherIcon from 'feather-icons-react';

const unitsApi = new UnitsApi();

interface Props {
  visible: boolean;
  categoryId: number;
  categoryHasFloors: boolean;
  floors: UnitCategoryFloorDto[];
  unit?: UnitBasicInfoDto;
  statistics?: UnitStatisticsDto;
  onCancel: () => void;
  onSuccess: () => Promise<void>;
}

export const UnitCreateModal: React.FC<Props> = ({
  visible,
  categoryId,
  categoryHasFloors,
  floors,
  unit,
  statistics,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEditMode = !!unit;

  useEffect(() => {
    if (visible) {
      if (isEditMode && unit) {
        // In edit mode, map existing floor allocations
        const initialAllocations = floors.map(floor => ({
          floorId: floor.id,
          stock: floor.unitsCount || 0,
          description: floor.description,
          floorNumber: floor.floorNumber,
        }));

        form.setFieldsValue({
          name: unit.name,
          description: unit.description,
          squareMeters: unit.squareMeters,
          stock: categoryHasFloors ? undefined : unit.stock,
          floorAllocations: initialAllocations,
        });
      } else {
        // In create mode, initialize with empty allocations
        form.setFieldsValue({
          floorAllocations: floors.map(floor => ({
            floorId: floor.id,
            stock: 0,
            description: floor.description,
            floorNumber: floor.floorNumber,
          })),
        });
      }
    }
  }, [visible, unit, floors, isEditMode, categoryHasFloors]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const floorAllocations: CreateUpdateUnitFloorAllocationDto[] = values.floorAllocations
        ? values.floorAllocations
          .filter((allocation: any) => allocation.stock > 0)
          .map((allocation: any) => ({
            categoryFloorId: allocation.floorId,
            stock: Number(allocation.stock),
          }))
        : [];

      if (isEditMode && unit) {
        await unitsApi.apiUnitsIdPut({
          id: unit.id!,
          updateUnitCommand: {
            id: unit.id!,
            name: values.name,
            stock: floorAllocations.length > 0 ? undefined : values.stock,
            description: values.description,
            squareMeters: values.squareMeters,
            floorAllocations,
          },
        });
      } else {
        await unitsApi.apiUnitsPost({
          createUnitCommand: {
            name: values.name,
            description: values.description,
            squareMeters: values.squareMeters,
            stock: floorAllocations.length > 0 ? undefined : values.stock,
            categoryId: categoryId,
            floorAllocations,
          },
        });
      }

      openNotificationWithIcon(
        'success',
        t(isEditMode ? 'units:updated' : 'units:created', `Unit ${isEditMode ? 'updated' : 'created'} successfully`),
      );
      form.resetFields();
      await onSuccess();
      onCancel();
    } catch (error) {
      console.log(error);
      openNotificationWithIcon(
        'error',
        t(
          isEditMode ? 'units:update-error' : 'units:create-error',
          `Failed to ${isEditMode ? 'update' : 'create'} unit`,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  // Calculate minimum stock based on reserved and sold units
  const getMinimumStock = () => {
    if (!statistics) return 0;
    return (statistics.reserved || 0) + (statistics.sold || 0);
  };

  const minimumStock = getMinimumStock();

  return (
    <Modal
      title={t(isEditMode ? 'units:edit' : 'units:create-new', isEditMode ? 'Edit Unit' : 'Create New Unit')}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={t('units:name', 'Name')}
              name="name"
              rules={[{ required: true, message: t('units:name-required', 'Name is required') }]}
            >
              <Input prefix={<FeatherIcon icon="box" size={16} />} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('units:square-meters', 'Square Meters')}
              name="squareMeters"
              rules={[{ required: true, message: t('units:square-meters-required', 'Square meters is required') }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} prefix={<FeatherIcon icon="maximize-2" size={16} />} />
            </Form.Item>
          </Col>
          {!categoryHasFloors && (
            <Col span={12}>
              <Form.Item
                label={t('units:stock', 'Stock')}
                name="stock"
                rules={[
                  { required: true, message: t('units:stock-required', 'Stock is required') },
                  {
                    validator: async (_, value) => {
                      if (statistics && value < minimumStock) {
                        throw new Error(
                          t(
                            'units:stock-minimum-error',
                            'Stock cannot be less than reserved ({{reserved}}) + sold ({{sold}}) units',
                            { reserved: statistics.reserved || 0, sold: statistics.sold || 0 },
                          ),
                        );
                      }
                    },
                  },
                ]}
                extra={
                  statistics && (
                    <div style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.45)' }}>
                      {t('units:stock-info', 'Reserved: {{reserved}}, Sold: {{sold}}, Current: {{current}}', {
                        reserved: statistics.reserved || 0,
                        sold: statistics.sold || 0,
                        current: statistics.currentStock || 0,
                      })}
                    </div>
                  )
                }
              >
                <InputNumber
                  min={minimumStock}
                  style={{ width: '100%' }}
                  prefix={<FeatherIcon icon="package" size={16} />}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Form.Item name="description" label={t('units:description', 'Description')}>
          <Input.TextArea rows={3} placeholder={t('units:enter-description', 'Enter description')} />
        </Form.Item>

        {categoryHasFloors && (
          <Form.List
            name="floorAllocations"
            initialValue={floors.map(floor => ({
              floorId: floor.id,
              stock: floor.unitsCount || 0,
              description: floor.description,
              floorNumber: floor.floorNumber,
            }))}
          >
            {fields => (
              <div>
                <div style={{ marginBottom: 16, fontWeight: 500 }}>
                  {t('units:floor-allocations-title', 'Floor Allocations')}
                </div>
                <Table
                  dataSource={fields}
                  pagination={false}
                  rowKey="key"
                  size="small"
                  columns={[
                    {
                      title: t('units:floor', 'Floor'),
                      width: '60%',
                      render: (_, field) => {
                        const floor = floors.find(
                          f => f.id === form.getFieldValue(['floorAllocations', field.name, 'floorId']),
                        );
                        return (
                          <div>
                            <div style={{ fontWeight: 500 }}>
                              {floor?.description ||
                                t('categories:floor-number', 'Floor {{number}}', { number: floor?.floorNumber })}
                            </div>
                            {floor?.description && (
                              <div style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.45)' }}>
                                {t('categories:floor-number', 'Floor {{number}}', { number: floor?.floorNumber })}
                              </div>
                            )}
                          </div>
                        );
                      },
                    },
                    {
                      title: t('units:stock', 'Stock'),
                      width: '40%',
                      render: (_, field) => {
                        const floor = floors.find(
                          f => f.id === form.getFieldValue(['floorAllocations', field.name, 'floorId']),
                        );

                        // Calculate minimum stock for this specific floor if we have floor-specific data
                        const floorMinStock = isEditMode ?
                          (floor?.reservedUnitsCount || 0) + (floor?.soldUnitsCount || 0) : 0;

                        return (
                          <Form.Item
                            name={[field.name, 'stock']}
                            style={{ marginBottom: 0 }}
                            rules={[
                              {
                                validator: async (_, value) => {
                                  if (isEditMode && value < floorMinStock) {
                                    throw new Error(
                                      t('units:floor-stock-minimum-error',
                                        'Cannot be less than {{min}} (reserved + sold)',
                                        { min: floorMinStock }
                                      ),
                                    );
                                  }
                                },
                              },
                            ]}
                          >
                            <InputNumber
                              min={floorMinStock}
                              style={{ width: '100%' }}
                              placeholder={t('units:enter-stock', 'Enter stock')}
                            />
                          </Form.Item>
                        );
                      },
                    },
                  ]}
                />
                {isEditMode && (
                  <div style={{ marginTop: 8, fontSize: '12px', color: 'rgba(0, 0, 0, 0.45)' }}>
                    {t('units:floor-allocation-note', 'Note: Stock cannot be reduced below reserved + sold units per floor')}
                  </div>
                )}
              </div>
            )}
          </Form.List>
        )}

        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>{t('global:cancel', 'Cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t(isEditMode ? 'global:update' : 'global:create', isEditMode ? 'Update' : 'Create')}
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};